import { useMutation } from '@apollo/client';
import type {
  UpsertUserSearchPreferencesMutation,
  UpsertUserSearchPreferencesMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpsertUserSearchPreferencesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useUpsertUserSearchPreferencesTRPC } from './useUpsertUserSearchPreferencesTRPC';

/**
 * Flag-gated wrapper for the `upsertUserSearchPreferences` mutation. Defaults to
 * the legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Both paths return the same
 * `UpsertUserSearchPreferencesMutation` shape so the consumer doesn't branch.
 */
export const useUpsertUserSearchPreferences = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [upsertGraphQL, graphqlState] = useMutation(
    UpsertUserSearchPreferencesDocument
  );

  const trpcMutation = useUpsertUserSearchPreferencesTRPC();

  const upsertUserSearchPreferences = async (
    input: UpsertUserSearchPreferencesMutationVariables
  ): Promise<UpsertUserSearchPreferencesMutation> => {
    if (trpcEnabled) {
      return trpcMutation.upsertUserSearchPreferences(input);
    }

    const result = await upsertGraphQL({ variables: input });
    if (!result.data) {
      throw new Error('Failed to upsert user search preferences');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      upsertUserSearchPreferences,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    upsertUserSearchPreferences,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
